import React from "react";
import { TableColumnsItem } from "../../interfaces/components/mainTable";
import { ColumnType, ButtonType } from "../utils/enums";
import { Medication } from "../../interfaces/api/medications";
import { Trash2 } from "lucide-react";

interface MedicationColumnHandlers {
  onDelete: (medication: Medication) => void;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

const getActionButtonClasses = (type: string) => {
  const baseClasses = "p-2 rounded-full transition-colors duration-200";
  switch (type) {
    case "delete":
      return `${baseClasses} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10`;
    default:
      return `${baseClasses} text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-500/10`;
  }
};

export const getMedicationsTableColumns = (
  handlers: MedicationColumnHandlers,
): TableColumnsItem[] => [
  {
    key: "name",
    name: "Medication Name",
    type: ColumnType.TEXT,
    width: "200px",
    cardTitle: true,
    customCellClass: "font-medium text-gray-900 dark:text-gray-100",
  },
  {
    key: "dosage",
    name: "Dosage",
    type: ColumnType.TEXT,
    width: "120px",
    cardOrder: 2,
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "frequency",
    name: "Frequency",
    type: ColumnType.TEXT,
    width: "120px",
    cardOrder: 3,
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "start_date",
    name: "Start Date",
    type: ColumnType.CUSTOM,
    width: "120px",
    cardOrder: 4,
    customValue: (row: Medication) => formatDate(row.start_date),
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "end_date",
    name: "End Date",
    type: ColumnType.CUSTOM,
    width: "120px",
    cardOrder: 5,
    customValue: (row: Medication) => formatDate(row.end_date),
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "route",
    name: "Route",
    type: ColumnType.TEXT,
    width: "120px",
    cardOrder: 6,
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "is_active",
    name: "Status",
    type: ColumnType.CUSTOM,
    width: "100px",
    cardOrder: 7,
    customValue: (row: Medication) => (row.is_active ? "Active" : "Inactive"),
    customCellClass: "text-gray-600 dark:text-gray-400",
  },
  {
    key: "actions",
    name: "Actions",
    type: ColumnType.BUTTON,
    width: "100px",
    cardOrder: 999,
    buttonsList: [
      {
        type: ButtonType.ICON,
        icon: () => <Trash2 className="w-4 h-4" />,
        onPress: (e: React.MouseEvent, row: Medication) => {
          e.stopPropagation();
          handlers.onDelete(row);
        },
        cssClass: getActionButtonClasses("delete"),
      },
    ],
  },
];
